// ./src/utils/api/TravelHubService.ts
//
// Travel hub service — the GLOBAL catalog of airports / stations and the IANA
// timezone each sits in. Reads only: rows come from the seeded US/CA airport list
// and (phase 2) from a server-side Google Places + Time Zone API resolve that
// writes through and caches. See docs/event-travel-architecture.md.
//
// This is what makes an itinerary's timezones correct without asking anyone to
// know that Los Angeles is "America/Los_Angeles".
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/travel.ts)
// =============================================================================

export const TRAVEL_HUB_KIND = {
  AIRPORT: "airport",
  TRAIN_STATION: "train_station",
  BUS_STATION: "bus_station",
  FERRY_TERMINAL: "ferry_terminal",
  OTHER: "other",
} as const;

export const TRAVEL_HUB_KIND_VALUES = Object.values(TRAVEL_HUB_KIND);

export const TRAVEL_HUB_SOURCE = {
  SEED: "seed",
  GOOGLE: "google",
} as const;

// The catalog is a few hundred rows (~25KB), so the picker loads it ONCE and
// filters locally — server-searching per keystroke fought Mantine's own search
// state and made the input reset as you typed. Revisit only if Google resolves
// push this past a few thousand rows. (#118: `listAll` used to be ONE page of 1000 — it reads the whole catalog now.)

// =============================================================================
// GraphQL
// =============================================================================

const TRAVEL_HUB_FIELDS = `
  id
  code
  name
  city
  regionCode
  countryCode
  timezone
  latitude
  longitude
  kind
  source
  status
`;

const TRAVEL_HUB_LIST_QUERY = gql`
  query TravelHubList($filter: TravelHubFilter, $params: PaginationAndSorting) {
    travelHubList(filter: $filter, params: $params) {
      code
      data { ${TRAVEL_HUB_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TRAVEL_HUB_GET_MANY_QUERY = gql`
  query TravelHubGetMany($ids: [ID]) {
    travelHubGetMany(ids: $ids) {
      code
      data { ${TRAVEL_HUB_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api TravelHub schema)
// =============================================================================

export interface TravelHub {
  id?: string;
  /** IATA / station code, e.g. "LAX". Null when the place has none. */
  code?: string | null;
  name?: string | null;
  city?: string | null;
  regionCode?: string | null;
  countryCode?: string | null;
  /** IANA zone, e.g. "America/Los_Angeles" — never a UTC offset. */
  timezone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  kind?: string | null;
  source?: string | null;
  status?: number;
}

export interface TravelHubFilter {
  id?: string[] | null;
  code?: string[] | null;
  kind?: string[] | null;
  countryCode?: string[] | null;
  source?: string[] | null;
  searchKeyword?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class TravelHubService {
  static async list(
    filter?: TravelHubFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(TRAVEL_HUB_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeQuery(TRAVEL_HUB_GET_MANY_QUERY, { ids });
  }

  /**
   * The whole catalog — the picker filters it locally. ⚠ ROADMAP #118: this was ONE page of 1000 under the name
   * `listAll`, so the 1001st hub (Google resolves add to the catalog) was simply not offered. It pages now.
   */
  static async listAll(filter?: TravelHubFilter): Promise<OpResult> {
    return listAllPages<TravelHub>(TravelHubService.list, filter);
  }
}

// =============================================================================
// Display helpers
// =============================================================================

/** "LAX — Los Angeles International Airport" (code first: it is what people type). */
export const travelHubLabel = (hub?: TravelHub | null): string => {
  if (!hub) {
    return "";
  }

  const name = hub.name || "";

  if (!hub.code) {
    return name;
  }

  return name ? `${hub.code} — ${name}` : hub.code;
};

/** "Los Angeles, CA · America/Los_Angeles" — the secondary line under a hub option. */
export const travelHubDescription = (hub?: TravelHub | null): string => {
  if (!hub) {
    return "";
  }

  const place = [hub.city, hub.regionCode].filter(Boolean).join(", ");

  return [place, hub.timezone].filter(Boolean).join(" · ");
};
